import { BACKEND_URL } from '@/config';
﻿import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/context/ToastContext';
import axios from 'axios';
import {
  Plus, Search, Package, Download, MessageSquare, MapPin, Trash2, Pencil, Copy, Star, Eye, Ban,
  AlertTriangle, Send, Navigation, Weight, Calendar, Coins, Image as ImageIcon, ClockAlert, RefreshCw,
} from 'lucide-react';
import MissionListCard from '@/components/MissionListCard';
import ModernAnnonceCard from '@/components/ModernAnnonceCard';
import { MissionListCardSkeleton } from '@/components/Skeletons';
import { cityFlag as flag } from '@/utils/cityFlag';
import ProposeModal from '@/components/ProposeModal';
import { CCY_SYM, ccySym } from '@/utils/currencyUtils';
import { MISSIONS_API_LIMIT, buildActiveMissionIds } from '@/utils/missionGuards';
import '@/styles/skeletons.css';
import { isEntreprise, isEntreprisePending } from '@/utils/entreprise';
import { photoCount } from '@/utils/photos';
import { hasRole, PUBLISH, SENDER } from '@/utils/roles';

const API = BACKEND_URL + '/api';
axios.defaults.withCredentials = true;

const SHIPMENT_STATUS = {
  DRAFT:          { label: 'Brouillon',    tone: 'gray' },
  PENDING_REVIEW: { label: 'En attente',   tone: 'blue' },
  PUBLISHED:      { label: 'Publié',       tone: 'teal' },
  ASSIGNED:       { label: 'Assigné',      tone: 'teal' },
  IN_PROGRESS:    { label: 'Pris en charge', tone: 'gold' },
  IN_TRANSIT:     { label: 'En transport',  tone: 'gold' },
  DELIVERED:      { label: 'Livré',         tone: 'teal' },
  COMPLETED:      { label: 'Livré',         tone: 'teal' },
  DISPUTED:       { label: 'Litige',       tone: 'red' },
  ARCHIVED:       { label: 'Archivé',      tone: 'gray' },
  CANCELLED:      { label: 'Annulée',      tone: 'red' },
  REJECTED:       { label: 'Rejeté',       tone: 'red' },
  EXPIRED:        { label: 'Expirée',      tone: 'gray' },
};

function formatDate(d) {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
}

const FILTERS = [
  { v: 'all',        l: 'Toutes' },
  { v: 'this_month', l: 'Ce mois' },
  { v: 'active',     l: 'En cours' },
];

const STATUS_OPTIONS = [
  { v: '',               l: 'Tous statuts' },
  { v: 'DRAFT',          l: 'Brouillon' },
  { v: 'PENDING_REVIEW', l: 'En attente' },
  { v: 'PUBLISHED',      l: 'Publié' },
  { v: 'IN_PROGRESS',    l: 'Pris en charge' },
  { v: 'IN_TRANSIT',     l: 'En transport' },
  { v: 'COMPLETED',      l: 'Livré' },
  { v: 'DISPUTED',       l: 'Litige' },
  { v: 'REJECTED',       l: 'Rejeté' },
  { v: 'CANCELLED',      l: 'Annulé' },
  { v: 'EXPIRED',        l: 'Expirée' },
];

/* ── Helpers pour normaliser un trajet public vers le format ModernAnnonceCard ── */
function daysFromNow(dateIso) {
  if (!dateIso) return '·';
  const diff = Math.ceil((new Date(dateIso) - Date.now()) / 86400000);
  if (diff < 0) return `J${diff}`;
  if (diff === 0) return 'Auj.';
  return `J-${diff}`;
}
const AV_COLORS = ['#1a4a7a', '#0e7a68', '#C8832A', '#4B44C8', '#1B4332', '#B91C1C'];
const avColor = (id) => AV_COLORS[(String(id || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0)) % AV_COLORS.length];
const initials = (f, l) => `${(f || '?')[0]}${(l || '?')[0]}`.toUpperCase();

function normalizeTrip(item) {
  const u = item.author || item.owner || {};
  const score = u.avg_rating ? u.avg_rating.toFixed(1).replace('.', ',') : null;
  return {
    raw: item, id: item.id, type: 'trajet',
    from: item.departure_city, to: item.arrival_city,
    days: daysFromNow(item.departure_date),
    daysNum: !item.departure_date ? null : Math.ceil((new Date(item.departure_date) - Date.now()) / 86400000),
    price: item.price_per_kg,
    unit: '€/kg',
    weight: item.available_weight_kg,
    hot: (() => { const d = Math.ceil((new Date(item.departure_date) - Date.now()) / 86400000); return d > 0 && d <= 5; })(),
    user: {
      id: item.user_id,
      name: `${u.first_name || 'Utilisateur'} ${(u.last_name_initial || u.last_name || '').slice(0, 1).toUpperCase()}.`.trim(),
      ini: initials(u.first_name, u.last_name_initial || u.last_name),
      color: avColor(item.user_id),
      score: score || 'Nouveau', trips: u.rating_count || 0,
      verified: u.is_verified || false, pro: isEntreprise(u), proPending: isEntreprisePending(u), subscribed: !!u.pro_subscribed,
    },
  };
}

export default function ShipmentsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  /* ── Onglet actif ── */
  const location = useLocation();
  const [tab, setTab] = useState(location.state?.openTab || 'mine');

  /* ── Annonces (onglet Mes Annonces) ── */
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeMissionShipmentIds, setActiveMissionShipmentIds] = useState(new Set());
  const [statusFilter, setStatusFilter] = useState('');
  const [presetFilter, setPresetFilter] = useState('all');
  const [proposeTarget, setProposeTarget] = useState(null);
  const [pendingConfirm, setPendingConfirm] = useState(null); // { id, action: 'delete'|'cancel' }

  /* ── Favoris trajets (onglet Mes Favoris) ── */
  const [favs, setFavs] = useState(() => {
    try { return JSON.parse(localStorage.getItem('c2s_favs') || '[]'); } catch { return []; }
  });
  const [favoriteTrips, setFavoriteTrips] = useState([]);
  const [loadingFavs, setLoadingFavs] = useState(false);

  /* ── Chargement des annonces ── */
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [shipmentsRes, missionsRes] = await Promise.all([
        axios.get(`${API}/shipments/mine`),
        axios.get(`${API}/missions?limit=${MISSIONS_API_LIMIT}`).catch(() => ({ data: { data: [] } })),
      ]);
      setShipments(shipmentsRes.data || []);
      setActiveMissionShipmentIds(buildActiveMissionIds(missionsRes.data?.data || [], 'shipment_id'));
    } catch { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Chargement des favoris (trajets publics filtrés par localStorage) ── */
  const loadFavorites = useCallback(async () => {
    setLoadingFavs(true);
    try {
      const currentFavs = (() => { try { return JSON.parse(localStorage.getItem('c2s_favs') || '[]'); } catch { return []; } })();
      if (currentFavs.length === 0) { setFavoriteTrips([]); setFavs([]); return; }

      const [p1, p2] = await Promise.all([
        axios.get(`${API}/public/trips?limit=50&page=1`).catch(() => ({ data: { data: [] } })),
        axios.get(`${API}/public/trips?limit=50&page=2`).catch(() => ({ data: { data: [] } })),
      ]);
      const all = [...(p1.data?.data || []), ...(p2.data?.data || [])];
      const seen = new Set();
      const faved = all
        .filter(t => { if (currentFavs.includes(t.id) && !seen.has(t.id)) { seen.add(t.id); return true; } return false; })
        .map(normalizeTrip);
      setFavoriteTrips(faved);
      setFavs(currentFavs);
    } catch { toast.error('Erreur de chargement des favoris'); }
    finally { setLoadingFavs(false); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Retirer un favori ── */
  const toggleFav = useCallback((id) => {
    setFavs(prev => {
      const next = prev.filter(x => x !== id);
      localStorage.setItem('c2s_favs', JSON.stringify(next));
      return next;
    });
    setFavoriteTrips(prev => prev.filter(t => t.id !== id));
  }, []);

  useEffect(() => {
    if (tab === 'mine') load();
    else loadFavorites();
  }, [tab, load, loadFavorites]);

  const filtered = useMemo(() => {
    const startOfMonth = new Date(); startOfMonth.setDate(1); startOfMonth.setHours(0, 0, 0, 0);
    return shipments.filter(s => {
      if (statusFilter && s.status !== statusFilter) return false;
      if (presetFilter === 'this_month' && new Date(s.created_at || 0) < startOfMonth) return false;
      if (presetFilter === 'active' && !['PUBLISHED', 'IN_PROGRESS'].includes(s.status)) return false;
      return true;
    });
  }, [shipments, statusFilter, presetFilter]);

  const stats = useMemo(() => ({
    total:  shipments.length,
    active: shipments.filter(s => ['PUBLISHED', 'IN_PROGRESS'].includes(s.status)).length,
  }), [shipments]);

  const handleDelete = async (id) => {
    setPendingConfirm(null);
    try {
      await axios.delete(`${API}/shipments/${id}`);
      toast.success('Annonce supprimée.');
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail || '';
      if (detail.toLowerCase().includes('mission')) {
        toast.error('Suppression impossible : une mission est en cours sur cette annonce.');
      } else {
        toast.error(detail || 'Erreur lors de la suppression');
      }
    }
  };

  const handleDuplicate = (s) => {
    sessionStorage.setItem('c2s_shipment_duplicate', JSON.stringify({
      title: s.title, description: s.description,
      departure_city: s.departure_city, arrival_city: s.arrival_city,
      departure_country: s.departure_country || '',
      arrival_country: s.arrival_country || '',
      weight_kg: s.weight_kg, dimensions: s.dimensions,
      price_offered: s.price_offered,
      primary_currency: s.primary_currency || 'EUR',
      currencies: s.currencies || ['EUR'],
      categories: s.categories || [],
      category_custom: s.category_custom || '',
      constraints: s.constraints || [],
      constraint_custom: s.constraint_custom || '',
      carrier_preferences: s.carrier_preferences || [],
      message_to_carriers: s.message_to_carriers || '',
    }));
    navigate('/app/shipments/new?duplicate=1');
  };

  const handleExport = () => {
    const rows = [['Titre', 'Départ', 'Arrivée', 'Poids (kg)', 'Prix (€)', 'Statut', 'Créée']];
    filtered.forEach(s => rows.push([
      s.title || '', s.departure_city || '', s.arrival_city || '',
      s.weight_kg || '', s.price_offered || '', s.status || '',
      s.created_at ? new Date(s.created_at).toLocaleDateString('fr-FR') : '',
    ]));
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `mes-annonces-${Date.now()}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Export CSV téléchargé.');
  };

  const handleCancel = async (id) => {
    setPendingConfirm(null);
    try {
      await axios.post(`${API}/shipments/${id}/cancel`);
      toast.success('Annonce annulée.');
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Erreur'); }
  };

  const buildActions = (s) => {
    const acts = [];
    const hasActiveMission = activeMissionShipmentIds.has(s.id);
    if (s.status === 'IN_PROGRESS') {
      acts.push({ key: 'msg',    icon: <MessageSquare size={16} />, label: 'Messagerie',           testid: `sc-msg-${s.id}`,    onClick: () => navigate('/app/messages') });
      acts.push({ key: 'track',  icon: <MapPin size={16} />,        label: 'Suivi',                testid: `sc-track-${s.id}`,  onClick: () => navigate(`/app/shipments/${s.id}`) });
      acts.push({ key: 'report', icon: <AlertTriangle size={16} />, label: 'Signaler un problème', testid: `sc-report-${s.id}`, onClick: () => navigate(`/app/shipments/${s.id}#report`) });
    } else if (s.status === 'PUBLISHED') {
      acts.push({ key: 'matchs', icon: <Search size={16} />, label: 'Voir les matchs', testid: `sc-matchs-${s.id}`, onClick: () => navigate(`/app/shipments/${s.id}/matching`) });
      acts.push({ key: 'edit',   icon: <Pencil size={16} />, label: 'Modifier',        testid: `sc-edit-${s.id}`,   onClick: () => navigate(`/app/shipments/${s.id}/edit`) });
      acts.push({ key: 'cancel', icon: <Ban size={16} />,    label: 'Annuler',         testid: `sc-cancel-${s.id}`, onClick: () => setPendingConfirm({ id: s.id, action: 'cancel' }) });
    } else if (s.status === 'PENDING_REVIEW') {
      acts.push({ key: 'view',   icon: <Eye size={16} />,    label: 'Voir',     testid: `sc-view-${s.id}`,   onClick: () => navigate(`/app/shipments/${s.id}`) });
      acts.push({ key: 'edit',   icon: <Pencil size={16} />, label: 'Modifier', testid: `sc-edit-${s.id}`,   onClick: () => navigate(`/app/shipments/${s.id}/edit`) });
      acts.push({ key: 'cancel', icon: <Ban size={16} />,    label: 'Annuler',  testid: `sc-cancel-${s.id}`, onClick: () => setPendingConfirm({ id: s.id, action: 'cancel' }) });
    } else if (['DELIVERED', 'COMPLETED'].includes(s.status)) {
      acts.push({ key: 'view', icon: <Eye size={16} />,  label: 'Voir',                    testid: `sc-view-${s.id}`, onClick: () => navigate(`/app/shipments/${s.id}`) });
      acts.push({ key: 'dup',  icon: <Copy size={16} />, label: 'Dupliquer',               testid: `sc-dup-${s.id}`,  onClick: () => handleDuplicate(s) });
      acts.push({ key: 'rate', icon: <Star size={16} />, label: 'Noter le transporteur',   testid: `sc-rate-${s.id}`, onClick: () => navigate(`/app/shipments/${s.id}#rate`) });
    } else if (s.status === 'DRAFT') {
      acts.push({ key: 'edit', icon: <Pencil size={16} />, label: 'Modifier',      testid: `sc-edit-${s.id}`,   onClick: () => navigate(`/app/shipments/${s.id}/edit`) });
      acts.push({ key: 'view', icon: <Eye size={16} />,    label: 'Prévisualiser', testid: `sc-view-${s.id}`,   onClick: () => navigate(`/app/shipments/${s.id}`) });
      acts.push({ key: 'del',  icon: <Trash2 size={16} />, label: 'Supprimer',     testid: `sc-del-${s.id}`,    onClick: () => setPendingConfirm({ id: s.id, action: 'delete' }), disabled: hasActiveMission, tooltip: hasActiveMission ? 'Suppression impossible : mission en cours' : undefined });
    } else if (s.status === 'REJECTED') {
      acts.push({ key: 'view', icon: <Eye size={16} />,    label: 'Voir motif',            testid: `sc-view-${s.id}`, onClick: () => navigate(`/app/shipments/${s.id}`) });
      acts.push({ key: 'dup',  icon: <Copy size={16} />,   label: 'Republier (dupliquer)', testid: `sc-dup-${s.id}`,  onClick: () => handleDuplicate(s) });
      acts.push({ key: 'del',  icon: <Trash2 size={16} />, label: 'Supprimer',             testid: `sc-del-${s.id}`,  onClick: () => setPendingConfirm({ id: s.id, action: 'delete' }), disabled: hasActiveMission, tooltip: hasActiveMission ? 'Suppression impossible : mission en cours' : undefined });
    } else if (s.status === 'EXPIRED') {
      // Date passée (2026-10-28) : republier la même annonce avec une nouvelle date, ou en créer une copie
      acts.push({ key: 'republish', icon: <RefreshCw size={16} />, label: 'Republier avec une nouvelle date', testid: `sc-republish-${s.id}`, onClick: () => navigate(`/app/shipments/${s.id}/edit`) });
      acts.push({ key: 'dup',  icon: <Copy size={16} />,   label: 'Dupliquer', testid: `sc-dup-${s.id}`,  onClick: () => handleDuplicate(s) });
      acts.push({ key: 'view', icon: <Eye size={16} />,    label: 'Voir',      testid: `sc-view-${s.id}`, onClick: () => navigate(`/app/shipments/${s.id}`) });
    } else if (s.status === 'CANCELLED') {
      acts.push({ key: 'view', icon: <Eye size={16} />,    label: 'Voir',      testid: `sc-view-${s.id}`, onClick: () => navigate(`/app/shipments/${s.id}`) });
      acts.push({ key: 'dup',  icon: <Copy size={16} />,   label: 'Dupliquer', testid: `sc-dup-${s.id}`,  onClick: () => handleDuplicate(s) });
      acts.push({ key: 'del',  icon: <Trash2 size={16} />, label: 'Supprimer', testid: `sc-del-${s.id}`,  onClick: () => setPendingConfirm({ id: s.id, action: 'delete' }), disabled: hasActiveMission, tooltip: hasActiveMission ? 'Suppression impossible : mission en cours' : undefined });
    } else if (s.status === 'ASSIGNED') {
      acts.push({ key: 'view', icon: <Eye size={16} />,           label: 'Voir',       testid: `sc-view-${s.id}`, onClick: () => navigate(`/app/shipments/${s.id}`) });
      acts.push({ key: 'msg',  icon: <MessageSquare size={16} />, label: 'Messagerie', testid: `sc-msg-${s.id}`,  onClick: () => navigate('/app/messages') });
    } else if (s.status === 'DISPUTED') {
      acts.push({ key: 'msg',  icon: <MessageSquare size={16} />, label: 'Messagerie',  testid: `sc-msg-${s.id}`,  onClick: () => navigate('/app/messages') });
      acts.push({ key: 'view', icon: <Eye size={16} />,           label: 'Voir litige', testid: `sc-view-${s.id}`, onClick: () => navigate(`/app/shipments/${s.id}`) });
    } else {
      acts.push({ key: 'view', icon: <Eye size={16} />, label: 'Voir', testid: `sc-view-${s.id}`, onClick: () => navigate(`/app/shipments/${s.id}`) });
    }
    return acts;
  };

  const topbarTitle = tab === 'mine' ? 'Mes annonces' : 'Mes Favoris';
  const topbarSub = tab === 'mine'
    ? `${stats.total} annonce${stats.total > 1 ? 's' : ''} · ${stats.active} active${stats.active > 1 ? 's' : ''}`
    : `${favoriteTrips.length} trajet${favoriteTrips.length > 1 ? 's' : ''} en favori`;

  return (
    <div>
      {/* ── Topbar ── */}
      <div className="mlc-topbar">
        <div>
          <div className="c2s-topbar-title">{topbarTitle}</div>
          <div className="c2s-topbar-sub">{topbarSub}</div>
        </div>
        <div className="mlc-topbar-actions">
          {tab === 'mine' && (
            <button type="button" className="mlc-btn mlc-btn--ghost" onClick={handleExport} data-testid="btn-export-shipments">
              <Download size={15} /> Exporter
            </button>
          )}
        </div>
      </div>

      {/* Onglets — Mes Favoris | Mes Annonces (tous les utilisateurs) */}
      <div style={{ display: 'flex', gap: 2, marginBottom: 16, background: 'rgba(15,23,42,.06)', borderRadius: 8, padding: 4, width: 'fit-content' }}>
        {[['favoris', 'Mes Favoris'], ['mine', 'Mes annonces']].map(([v, l]) => (
          <button key={v} data-testid={`tab-${v}`}
            style={{ padding: '6px 16px', borderRadius: 6, border: 'none', fontWeight: 500, fontSize: 12.5, cursor: 'pointer', background: tab === v ? '#fff' : 'transparent', color: tab === v ? '#111827' : '#6B7280', fontFamily: 'var(--f-body)' }}
            onClick={() => setTab(v)}>{l}
          </button>
        ))}
      </div>

      {/* ── Onglet Mes Favoris (trajets) ── */}
      {tab === 'favoris' && (
        loadingFavs ? (
          <div className="mlc-grid-2" data-testid="favorites-skeleton">
            {Array.from({ length: 4 }).map((_, i) => <MissionListCardSkeleton key={i} />)}
          </div>
        ) : favoriteTrips.length === 0 ? (
          <div className="empty-state">
            <Navigation size={48} className="empty-state-icon" />
            <div className="empty-state-title">Aucun favori</div>
            <div className="empty-state-sub">
              Ajoutez des annonces trajet en favoris depuis la page{' '}
              <span
                style={{ color: 'var(--c2s-teal, #0e7a68)', cursor: 'pointer', textDecoration: 'underline' }}
                onClick={() => navigate('/app/annonces')}
              >Annonces</span>.
            </div>
          </div>
        ) : (
          <div className="mc-grid" data-testid="favorites-grid">
            {favoriteTrips.map((a, i) => (
              <ModernAnnonceCard
                key={a.id}
                a={a}
                index={i}
                isFav={favs.includes(a.id)}
                onFav={() => toggleFav(a.id)}
                onClick={() => navigate(`/app/trips/${a.id}`)}
              />
            ))}
          </div>
        )
      )}

      {/* ── Onglet Mes Annonces ── */}
      {tab === 'mine' && (
        <>
          <div className="mlc-filters" data-testid="shipments-filters">
            <div className="mlc-select-wrap">
              <select
                className="mlc-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                data-testid="filter-status"
              >
                {STATUS_OPTIONS.map(opt => (
                  <option key={opt.v} value={opt.v}>{opt.l}</option>
                ))}
              </select>
            </div>
            {FILTERS.map(f => (
              <button
                key={f.v}
                type="button"
                className={`mlc-filter${presetFilter === f.v ? ' on' : ''}`}
                onClick={() => setPresetFilter(f.v)}
                data-testid={`preset-${f.v}`}
              >
                {f.l}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="mlc-grid-2" data-testid="shipments-skeleton">
              {Array.from({ length: 4 }).map((_, i) => <MissionListCardSkeleton key={i} />)}
            </div>
          ) : filtered.length === 0 && shipments.length > 0 ? (
            // Des colis existent, mais les filtres les masquent tous : on ne dit pas « publiez votre premier colis »
            <div className="empty-state" data-testid="empty-filtered">
              <Package size={48} className="empty-state-icon" />
              <div className="empty-state-title">Aucun colis ne correspond à ce filtre</div>
              <div className="empty-state-sub">Vous avez {shipments.length} colis au total.</div>
              <button className="mlc-btn mlc-btn--primary" onClick={() => { setStatusFilter(''); setPresetFilter('all'); }} data-testid="empty-reset-filters">
                Voir tous mes colis
              </button>
            </div>
          ) : filtered.length === 0 ? (
            <div className="empty-state">
              <Package size={48} className="empty-state-icon" />
              <div className="empty-state-title">Aucun colis pour l'instant</div>
              <div className="empty-state-sub">Publiez votre premier colis.</div>
              {hasRole(user, SENDER) && (
                <button className="mlc-btn mlc-btn--primary" onClick={() => navigate(PUBLISH.colis.form)} data-testid="empty-publish-colis">
                  <Plus size={15} /> {PUBLISH.colis.label}
                </button>
              )}
            </div>
          ) : (
            <div className="mlc-grid-2" data-testid="shipments-grid">
              {filtered.map(s => {
                const ref = `#C2S-${(s.id || '').slice(-4).toUpperCase()}`;
                const photos = photoCount(s);
                const isUrgent = Array.isArray(s.constraints) && s.constraints.includes('urgent');
                const sublineParts = [
                  `${s.departure_city} → ${s.arrival_city}`,
                  formatDate(s.updated_at || s.created_at),
                  ref,
                ];
                const chips = [];
                if (s.weight_kg) chips.push({ icon: Weight, label: `${s.weight_kg} kg` });
                if (s.dimensions) chips.push({ icon: Package, label: s.dimensions });
                if (s.departure_date) chips.push({ icon: Calendar, label: formatDate(s.departure_date) });
                if (s.primary_currency && s.primary_currency !== 'EUR') chips.push({ icon: Coins, label: s.primary_currency });
                if (photos > 0) chips.push({ icon: ImageIcon, label: `${photos} photo${photos > 1 ? 's' : ''}` });
                if (isUrgent) chips.push({ icon: ClockAlert, label: 'Urgent' });

                const isConfirm = pendingConfirm?.id === s.id;
                return (
                  <MissionListCard
                    key={s.id}
                    testid={`shipment-card-${s.id}`}
                    route={s.title ? s.title : <>{flag(s.departure_city)} {s.departure_city} → {flag(s.arrival_city)} {s.arrival_city}</>}
                    subline={sublineParts.join(' · ')}
                    status={SHIPMENT_STATUS[s.status] || { label: s.status, tone: 'gray' }}
                    chips={chips}
                    amount={s.price_offered ? Number(s.price_offered).toFixed(0) : 'Budget libre'}
                    amountUnit={s.price_offered ? ccySym(s.primary_currency || 'EUR') : ''}
                    urgent={isUrgent}
                    actions={buildActions(s)}
                    onClick={() => navigate(`/app/shipments/${s.id}`)}
                    confirmBand={isConfirm ? {
                      message: pendingConfirm.action === 'delete'
                        ? 'Supprimer <em>définitivement</em> ?'
                        : 'Annuler cette annonce ?',
                      variant: 'red',
                      onConfirm: () => pendingConfirm.action === 'delete' ? handleDelete(s.id) : handleCancel(s.id),
                      onCancel: () => setPendingConfirm(null),
                    } : undefined}
                  />
                );
              })}
            </div>
          )}
        </>
      )}

      <ProposeModal
        open={!!proposeTarget}
        onClose={() => setProposeTarget(null)}
        meSide="carrier"
        target={proposeTarget}
        onSuccess={() => { setProposeTarget(null); toast.success('Proposition envoyée.'); }}
      />
    </div>
  );
}
